import { useCallback } from "react"
import { useMutation } from "urql"

import type { PromoteDraftFailure, PromoteDraftInput, PromoteDraftSubmissionMutation } from "@/graphql/codegen/graphql"
import { PromoteDraftSubmissionDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import "@/screens/Pricing/graphql/mutations"

interface UsePromoteDraftSubmissionReturn {
  promoteDraftSubmission: (input: PromoteDraftInput) => Promise<boolean>
  isPromoting: boolean
}

export const usePromoteDraftSubmission = (): UsePromoteDraftSubmissionReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const [{ fetching }, executeMutation] = useMutation<PromoteDraftSubmissionMutation, { input: PromoteDraftInput }>(
    PromoteDraftSubmissionDocument
  )

  const promoteDraftSubmission = useCallback(
    async (input: PromoteDraftInput): Promise<boolean> => {
      const { data, error } = await executeMutation({ input })
      const output = data?.priceChange.promoteDraft
      const hasError = error || output?.__typename === "PromoteDraftFailure"

      if (hasError) {
        const message = error?.message ?? (output as PromoteDraftFailure).error.message
        notify({ type: NotificationType.Error, message })
        return false
      }
      notify({ type: NotificationType.Success, message: "Price changes submitted" })
      return true
    },
    [executeMutation, notify]
  )

  return { promoteDraftSubmission, isPromoting: fetching }
}
